﻿// Roadmap Creation Form Component
import React, { useState, useMemo } from 'react';
import { Loader2, Navigation, Crosshair, Timer, Earth, Zap, Shield, Trophy, Crown, Sparkles, Target, Rocket, X, TrendingUp, Brain, Lightbulb } from 'lucide-react';
import { LANGUAGES, SKILL_LEVELS } from '../../constants';
import { FormInput, Select } from '../ui';
import skillsData from '../../constants/skills.json';
import { useSubscription } from '../../hooks/useSubscription';

interface RoadmapFormProps {
  onSubmit: (data: RoadmapFormData) => Promise<void>;
  loading: boolean;
  progress: {
    percentage: number;
    message: string;
    stage: string;
  };
  theme: 'light' | 'dark';
  isPro?: boolean;
  onUpgradeClick?: () => void;
  userEmail?: string | null;
  streakCount?: number;
}

export interface RoadmapFormData {
  goal: string;
  skillLevel: string; // Beginner, Intermediate, or Expert
  language: string;
}

export const RoadmapCreationForm: React.FC<RoadmapFormProps> = ({
  onSubmit,
  loading,
  progress,
  theme,
  isPro = false,
  onUpgradeClick,
  userEmail,
  streakCount
}) => {
  const [formData, setFormData] = useState<RoadmapFormData>({
    goal: '',
    skillLevel: 'Beginner',
    language: 'English'
  });
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSkills, setFilteredSkills] = useState<Array<{ skill: string; category: string; trending?: boolean; confidence?: number; why?: string; difficulty?: string; marketValue?: string }>>([]);
  const [showQuotaModal, setShowQuotaModal] = useState(false);
  const [isCustomSkill, setIsCustomSkill] = useState(false);
  const [customSkillValidation, setCustomSkillValidation] = useState<{
    isValid: boolean;
    isCustom: boolean;
    validationStatus: 'pending' | 'validated' | 'rejected';
    validationNotes?: string;
    similarSkills?: Array<{ skill: string; category: string }>;
  } | null>(null);
  const [showCustomSkillWarning, setShowCustomSkillWarning] = useState(false);
  const [validatingSkill, setValidatingSkill] = useState(false);
  const { subscription } = useSubscription(userEmail);

  const isDark = theme === 'dark';

  // Get all skills from all categories with enhanced metadata
  const allSkills = useMemo(() => {
    const skills: Array<{ skill: string; category: string; trending?: boolean; confidence?: number; why?: string; difficulty?: string; marketValue?: string }> = [];
    Object.entries(skillsData.categories).forEach(([category, data]: any) => {
      if (data.skills && Array.isArray(data.skills)) {
        data.skills.forEach((skillItem: any) => {
          // Handle both string and object skill formats
          const skillName = typeof skillItem === 'string' ? skillItem : skillItem?.name;
          if (skillName && typeof skillName === 'string') {
            skills.push({ 
              skill: skillName, 
              category, 
              trending: typeof skillItem === 'object' ? skillItem?.trending : false,
              confidence: typeof skillItem === 'object' ? skillItem?.confidenceIndicator : undefined,
              why: typeof skillItem === 'object' ? skillItem?.why : undefined,
              difficulty: typeof skillItem === 'object' ? skillItem?.difficulty : undefined,
              marketValue: typeof skillItem === 'object' ? skillItem?.marketValue : undefined
            });
          }
        });
      }
    });
    return skills;
  }, []);

  // Filter skills based on input with smart ranking
  const handleGoalChange = (value: string) => {
    setFormData({ ...formData, goal: value });
    setShowSuggestions(true);

    if (value.trim().length === 0) {
      setFilteredSkills([]);
      return;
    }

    const searchTerm = value.toLowerCase().trim();
    const matches = allSkills
      .filter(item => {
        // Ensure item.skill is a string before calling toLowerCase
        const skillStr = String(item.skill || '');
        return skillStr.toLowerCase().includes(searchTerm);
      })
      // Sort by: exact match, then trending, then confidence
      .sort((a, b) => {
        const aExact = String(a.skill || '').toLowerCase() === searchTerm ? 1 : 0;
        const bExact = String(b.skill || '').toLowerCase() === searchTerm ? 1 : 0;
        if (aExact !== bExact) return bExact - aExact;
        
        const aTrending = a.trending ? 1 : 0;
        const bTrending = b.trending ? 1 : 0;
        if (aTrending !== bTrending) return bTrending - aTrending;
        
        return (b.confidence || 0) - (a.confidence || 0);
      })
      .slice(0, 10); // Show top 10 matches

    setFilteredSkills(matches);
  };

  const handleSelectSkill = (skill: string) => {
    setFormData({ ...formData, goal: skill });
    setShowSuggestions(false);
    setFilteredSkills([]);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // Validate that goal is from the approved list
    const isValidGoal = allSkills.some(item => item.skill === formData.goal);
    if (!isValidGoal) {
      alert('Please select a valid skill or discipline from the suggestions.');
      return;
    }
    
    // Check for Pro gating before submission (client-side UX)
    // Only Beginner is free, Intermediate and Expert require Pro
    if ((formData.skillLevel === 'Expert' || formData.skillLevel === 'Intermediate') && !isPro) {
      if (onUpgradeClick) {
        onUpgradeClick();
        return;
      } else {
        alert(`${formData.skillLevel} level roadmaps are a Pro feature. Please upgrade to unlock them!`);
        return;
      }
    }

    // Check quota for free users
    if (!subscription?.isPro && subscription && subscription.roadmapCount >= 3) {
      setShowQuotaModal(true);
      return;
    }
    
    await onSubmit(formData);
  };

  return (
    <div className="max-w-2xl mx-auto py-4 animate-in">
      {/* Header - Psychology Enhanced */}
      <div className="text-center mb-8 md:mb-12 space-y-4">
        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border ${isDark ? 'bg-red-500/10 border-red-500/20' : 'bg-red-50 border-red-200'}`}>
          <Navigation className={`w-5 h-5 ${isDark ? 'text-red-400' : 'text-red-600'}`} />
          <span className={`text-sm font-medium ${isDark ? 'text-red-400' : 'text-red-600'}`}>Your Personal Learning Blueprint</span>
        </div>
        <h2 className="text-3xl md:text-5xl font-bold tracking-tight">
          <span className={isDark ? 'bg-gradient-to-r from-red-400 to-rose-400 bg-clip-text text-transparent' : 'bg-gradient-to-r from-red-600 to-rose-600 bg-clip-text text-transparent'}>Rewire Your Brain</span>
          <br />
          <span className={isDark ? 'text-white' : 'text-zinc-900'}>In 30 Days or Less</span>
        </h2>
        <p className={`text-lg max-w-md mx-auto ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
          Tell us what skill matters most to you. We'll create your step-by-step blueprint—with daily wins, accountability check-ins, and the resources winners use. Your breakthrough starts here.
        </p>
        
        {/* Psychology Insight */}
        <div className={`mt-6 p-4 rounded-xl border ${isDark ? 'bg-blue-500/10 border-blue-500/20' : 'bg-blue-50 border-blue-200'}`}>
          <div className="flex items-start gap-2">
            <Brain className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
            <p className={`text-xs font-medium ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
              <strong>Smart learning science:</strong> Personalized pacing, spaced repetition, and real-world practice—all designed for your goals.
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className={`p-6 md:p-8 rounded-3xl border transition-all ${
        isDark 
          ? 'bg-white/[0.03] border-white/5' 
          : 'bg-white border-zinc-200/80 shadow-xl'
      } space-y-8 md:space-y-10`}>

        {/* Loading Progress */}
        {loading && (
          <div className="space-y-6 py-4 animate-in slide-in-from-top-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-red-600 to-rose-600 flex items-center justify-center">
                  <Loader2 className="w-5 h-5 text-white animate-spin" />
                </div>
                <span className="text-lg font-semibold text-red-500">
                  Creating Your Roadmap
                </span>
              </div>
              <span className="text-2xl font-bold text-red-500">{progress.percentage}%</span>
            </div>

            {/* Progress Bar */}
            <div className="relative">
              <div className={`h-3 rounded-full overflow-hidden ${isDark ? 'bg-white/5' : 'bg-zinc-100'}`}>
                <div
                  className="h-full bg-gradient-to-r from-red-500 via-orange-500 to-rose-500 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${progress.percentage}%` }}
                />
              </div>
            </div>

            {/* Progress Message */}
            <div className="text-center">
              <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-zinc-900'}`}>
                {progress.message}
              </p>
            </div>

            {/* Loading Animation */}
            <div className="flex items-center justify-center gap-2 pt-2">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-red-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 bg-red-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-red-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
              <p className={`text-sm font-medium ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>
                Please be patient, we're creating your learning path...
              </p>
            </div>
          </div>
        )}

        {/* Form Fields */}
        <div className={`space-y-6 md:space-y-8 transition-all duration-300 ${loading ? 'opacity-30 pointer-events-none' : ''}`}>
          {/* Goal Input - Psychology Enhanced */}
          <div className="space-y-3 relative">
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm font-semibold text-red-500">
                <Crosshair className="w-4 h-4" />
                What's Your Next Transformation?
              </label>
              <span className="text-xs text-zinc-500">Step 1 of 3</span>
            </div>
            <div className="relative">
              <input
                type="text"
                value={formData.goal}
                onChange={(e) => handleGoalChange(e.target.value)}
                onFocus={() => formData.goal.length > 0 && setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                required
                placeholder="React, Python, AI, Leadership... Start typing to discover your path"
                className={`w-full p-4 md:p-5 rounded-2xl text-lg font-medium outline-none transition-all ${
                  isDark 
                    ? 'bg-white/5 border border-white/10 focus:border-red-500/50 focus:bg-white/10 text-white placeholder:text-zinc-500' 
                    : 'bg-white border border-zinc-400 focus:border-red-500 focus:bg-white text-zinc-900 placeholder:text-zinc-600'
                }`}
              />

              {/* Suggestions Dropdown */}
              {showSuggestions && filteredSkills.length > 0 && (
                <div className={`absolute top-full left-0 right-0 mt-2 rounded-xl border shadow-lg z-50 max-h-72 overflow-y-auto backdrop-blur-sm ${
                  isDark
                    ? 'bg-zinc-800/95 border-white/10'
                    : 'bg-white/95 border-zinc-200'
                }`}>
                  {filteredSkills.map((item, idx) => {
                    const categoryData = skillsData.categories[item.category as keyof typeof skillsData.categories];
                    const isTrending = item.trending;
                    
                    return (
                      <button
                        key={idx}
                        onClick={() => handleSelectSkill(item.skill)}
                        className={`w-full text-left px-4 py-3 flex items-center justify-between hover:bg-red-500/15 transition-all duration-200 border-b border-white/5 last:border-b-0 group ${
                          idx === 0 ? 'rounded-t-xl' : ''
                        } ${
                          idx === filteredSkills.length - 1 ? 'rounded-b-xl' : ''
                        }`}
                        type="button"
                      >
                        <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`font-semibold truncate ${isDark ? 'text-white' : 'text-zinc-900'}`}>
                              {item.skill}
                            </span>
                            {/* Trending Badge - Social Proof */}
                            {isTrending && (
                              <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/20 text-red-500 text-[10px] font-bold whitespace-nowrap flex-shrink-0">
                                <TrendingUp className="w-3 h-3" />
                                Trending
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-xs">
                            <span className={`${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                              {item.category}
                            </span>
                            {item.marketValue && (
                              <span className={`px-2 py-0.5 rounded bg-green-500/20 text-green-600 font-bold ${isDark ? 'text-green-400' : 'text-green-700'}`}>
                                {item.marketValue === 'very_high' ? '💰 High Demand' : 
                                 item.marketValue === 'high' ? '💵 Good Demand' : 
                                 item.marketValue === 'essential' ? '⭐ Essential' : 'Medium'}
                              </span>
                            )}
                            {item.confidence && (
                              <span className={`text-[9px] font-bold ${item.confidence >= 8 ? 'text-green-500' : item.confidence >= 6 ? 'text-yellow-500' : 'text-blue-500'}`}>
                                {item.confidence}/10 confidence
                              </span>
                            )}
                          </div>
                          {item.why && (
                            <p className={`text-xs mt-1 line-clamp-1 italic ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>
                              "{item.why}"
                            </p>
                          )}
                        </div>
                        <span className="text-lg ml-2 flex-shrink-0">
                          {categoryData?.icon}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            
            {/* Validation message */}
            {formData.goal && !allSkills.some(s => s.skill === formData.goal) && (
              <p className={`text-xs flex items-center gap-2 ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`}>
                <Lightbulb className="w-3 h-3" />
                Select a skill from the list above to continue your journey
              </p>
            )}
          </div>

          {/* Skill Level Selection - Psychology Enhanced */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm font-semibold text-red-500">
                <Target className="w-4 h-4" />
                Choose Your Path
              </label>
              <span className="text-xs text-zinc-500">Step 2 of 3</span>
            </div>
            
            {/* Psychology Insight */}
            <div className={`p-3 rounded-lg border text-xs ${isDark ? 'bg-amber-500/10 border-amber-500/20' : 'bg-amber-50 border-amber-200'}`}>
              <p className={isDark ? 'text-amber-300' : 'text-amber-800'}>
                <strong>Pro Tip:</strong> Start with Beginner. Momentum beats heroics. Build identity first.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {SKILL_LEVELS.map((lvl, idx) => {
                const isExpert = lvl.name === 'Expert';
                const isIntermediate = lvl.name === 'Intermediate';
                const isLocked = (isExpert || isIntermediate) && !isPro;

                // Define unique styling for each level
                const levelStyles = {
                  'Beginner': {
                    icon: Shield,
                    gradient: 'from-emerald-500 to-teal-500',
                    hoverGradient: 'hover:from-emerald-600 hover:to-teal-600',
                    shadow: 'shadow-emerald-500/30',
                    border: 'border-emerald-500/30',
                    bg: isDark ? 'bg-emerald-500/5' : 'bg-emerald-50',
                    iconColor: formData.skillLevel === lvl.name ? 'text-white' : (isDark ? 'text-emerald-400' : 'text-emerald-600'),
                    psychologyCopy: 'Start here. Build momentum. Identity before mastery.'
                  },
                  'Intermediate': {
                    icon: isLocked ? Sparkles : Rocket,
                    gradient: isLocked ? 'from-zinc-500 to-zinc-600' : 'from-red-500 to-orange-500',
                    hoverGradient: isLocked ? 'hover:from-zinc-600 hover:to-zinc-700' : 'hover:from-red-600 hover:to-orange-600',
                    shadow: isLocked ? 'shadow-zinc-500/10' : 'shadow-red-500/30',
                    border: isLocked ? 'border-red-500/50' : 'border-red-500/30',
                    bg: isLocked ? (isDark ? 'bg-red-500/5' : 'bg-red-50/50') : (isDark ? 'bg-red-500/5' : 'bg-red-50'),
                    iconColor: formData.skillLevel === lvl.name ? 'text-white' : (isLocked ? 'text-red-500' : (isDark ? 'text-red-400' : 'text-red-600')),
                    psychologyCopy: 'Level up. Real depth. Professional capability.'
                  },
                  'Expert': {
                    icon: isLocked ? Sparkles : Crown,
                    gradient: isLocked ? 'from-zinc-500 to-zinc-600' : 'from-amber-500 to-orange-500',
                    hoverGradient: isLocked ? 'hover:from-zinc-600 hover:to-zinc-700' : 'hover:from-amber-600 hover:to-orange-600',
                    shadow: isLocked ? 'shadow-zinc-500/10' : 'shadow-amber-500/30',
                    border: isLocked ? 'border-amber-500/50' : 'border-amber-500/30',
                    bg: isLocked ? (isDark ? 'bg-amber-500/5' : 'bg-amber-50/50') : (isDark ? 'bg-amber-500/5' : 'bg-amber-50'),
                    iconColor: formData.skillLevel === lvl.name ? 'text-white' : (isLocked ? 'text-amber-500' : (isDark ? 'text-amber-400' : 'text-amber-600')),
                    psychologyCopy: 'Go deep. Master complexity. Industry thought leader.'
                  }
                };

                const style = levelStyles[lvl.name as keyof typeof levelStyles];
                const Icon = style.icon;

                return (
                  <button
                    key={lvl.name}
                    type="button"
                    onClick={() => {
                      if (isLocked) {
                        onUpgradeClick?.();
                        return;
                      }
                      setFormData({ ...formData, skillLevel: lvl.name });
                    }}
                    className={
                      formData.skillLevel === lvl.name
                        ? `bg-gradient-to-br ${style.gradient} border-transparent text-white shadow-2xl ${style.shadow} scale-105 group relative p-6 rounded-2xl border-2 transition-all duration-300 flex flex-col gap-4 overflow-hidden`
                        : `${style.bg} ${style.border} hover:${style.border} hover:shadow-lg transform hover:scale-105 group relative p-6 rounded-2xl border-2 transition-all duration-300 flex flex-col gap-4 overflow-hidden`
                    }
                  >
                    {/* Animated background gradient */}
                    {formData.skillLevel === lvl.name && (
                      <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-50" />
                    )}

                    {isLocked && (
                      <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-bold shadow-sm">
                        PRO
                      </div>
                    )}
                    
                    {/* Icon badge */}
                    <div className={`relative w-14 h-14 rounded-xl flex items-center justify-center transition-all ${
                      formData.skillLevel === lvl.name
                        ? 'bg-white/20 backdrop-blur-sm'
                        : isDark ? 'bg-white/5' : 'bg-white'
                    }`}>
                      <Icon className={`w-7 h-7 transition-all ${style.iconColor}`} />
                    </div>

                    {/* Content */}
                    <div className="relative flex flex-col gap-2 text-left">
                      <div className="flex items-center justify-between">
                        <h3 className={`text-xl font-bold ${
                          formData.skillLevel === lvl.name
                            ? 'text-white drop-shadow-sm'
                            : isDark ? 'text-zinc-100' : 'text-zinc-900'
                        }`}>
                          {lvl.name}
                        </h3>
                        {formData.skillLevel === lvl.name && (
                          <Sparkles className="w-5 h-5 text-white animate-pulse" />
                        )}
                      </div>
                      <p className={`text-xs leading-relaxed font-medium italic ${
                        formData.skillLevel === lvl.name
                          ? 'text-white drop-shadow-sm'
                          : isDark ? 'text-zinc-300' : 'text-zinc-700'
                      }`}>
                        {style.psychologyCopy}
                      </p>
                      
                      {/* Duration badge */}
                      <div className={`inline-flex items-center gap-1 text-xs font-bold mt-2 px-2 py-1 rounded-lg w-fit ${
                        formData.skillLevel === lvl.name
                          ? 'bg-white/30 text-white backdrop-blur-sm'
                          : isDark ? 'bg-white/10 text-zinc-200 border border-white/10' : 'bg-white text-zinc-700 border border-zinc-200 shadow-sm'
                      }`}>
                        <Timer className="w-3 h-3" />
                        {lvl.name === 'Beginner' ? '1 month' : lvl.name === 'Intermediate' ? '3 months' : '6 months'}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Language Selection */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm font-semibold text-red-500">
                <Earth className="w-4 h-4" />
                Roadmap Language
              </label>
              <span className="text-xs text-zinc-500">Step 3 of 3</span>
            </div>
            <div className="grid grid-cols-4 gap-2 md:gap-3">
              {LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  type="button"
                  onClick={() => setFormData({ ...formData, language: lang.name })}
                  className={`py-3 md:py-4 rounded-xl text-sm font-semibold border transition-all ${
                    formData.language === lang.name
                      ? 'bg-gradient-to-r from-red-600 to-rose-600 border-transparent text-white shadow-lg'
                      : isDark
                      ? 'bg-white/5 border-white/10 hover:border-red-500/30 text-zinc-300'
                      : 'bg-white border-zinc-200 hover:border-red-300 text-zinc-600'
                  }`}
                  title={`Learn in ${lang.name}`}
                >
                  {lang.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Submit Button - Psychology Enhanced */}
        {(() => {
          const isValidGoal = allSkills.some(item => item.skill === formData.goal);
          const isButtonDisabled = loading || !isValidGoal;
          
          return (
            <button
              type="submit"
              disabled={isButtonDisabled}
              title={!isValidGoal ? 'Please select a valid skill from the suggestions' : ''}
              className="w-full py-5 md:py-6 bg-gradient-to-r from-red-600 via-orange-600 to-rose-600 text-white rounded-2xl font-bold text-lg flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-xl shadow-red-500/25 btn-glow hover:shadow-2xl hover:scale-105"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin" />
                  Crafting Your Transformation...
                </>
              ) : !isValidGoal && formData.goal ? (
                <>
                  ❌ Please select a skill
                </>
              ) : (
                <>
                  <Rocket className="w-5 h-5" />
                  Start My Journey
                </>
              )}
            </button>
          );
        })()}
      </form>

      {/* Quota Hit Modal */}
      {showQuotaModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`${isDark ? 'bg-zinc-900' : 'bg-white'} rounded-xl shadow-xl max-w-md w-full p-6`}>
            {/* Header */}
            <div className="text-center mb-4">
              <div className="text-4xl mb-2">🔥</div>
              <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-zinc-900'}`}>
                You've Hit Your Limit
              </h2>
            </div>
            
            {/* Body - Loss Aversion */}
            <div className="mb-6 space-y-3">
              <p className={`text-sm font-medium ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                You've proven you're serious. 3 roadmaps in one month. That takes commitment. 
                <strong> Free users stop here. One more? Upgrade.</strong>
              </p>
              
              {streakCount && streakCount >= 14 && (
                <div className={`p-3 rounded-lg border ${isDark ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-200'}`}>
                  <p className={`text-sm font-bold ${isDark ? 'text-red-400' : 'text-red-700'}`}>
                    ⚠️ Your {streakCount}-day identity is fragile.
                  </p>
                  <p className={`text-xs ${isDark ? 'text-red-300' : 'text-red-600'} mt-1`}>
                    One decision breaks everything or makes it permanent. Choose wisely.
                  </p>
                </div>
              )}
            </div>
            
            {/* Options */}
            <div className="space-y-3">
              <button
                onClick={() => {
                  setShowQuotaModal(false);
                  onUpgradeClick?.();
                }}
                className="w-full px-4 py-3 bg-gradient-to-r from-red-600 to-orange-600 text-white font-semibold rounded-lg hover:shadow-lg transition-all text-lg"
              >
                Protect It Now
              </button>
              
              <button
                onClick={() => setShowQuotaModal(false)}
                className={`w-full px-4 py-3 ${isDark ? 'bg-zinc-700 hover:bg-zinc-600' : 'bg-zinc-200 hover:bg-zinc-300'} ${isDark ? 'text-white' : 'text-zinc-900'} font-medium rounded-lg transition-colors`}
              >
                Lose It
              </button>
            </div>
            
            {/* Footer */}
            <p className={`text-xs text-center font-medium ${isDark ? 'text-zinc-400' : 'text-zinc-600'} mt-4`}>
              $4.99/week to keep going. Cancel anytime. But today? Choose momentum.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
